import {
  Box,
  Icon,
  Center,
  Heading,
  Text,
  Flex,
  Button,
  List,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  CloseButton
} from '@chakra-ui/react';
import Link from 'next/link';
import { openPopupWidget } from 'react-calendly';
import { URLS } from '@lib';
import { Plan, plans, ToolTips, comingSoon } from './plans-content';
import {
  CheckIcon,
  XIcon,
  QuestionMarkCircleIcon,
  PlusIcon
} from '@heroicons/react/solid';
import { Tooltip } from '@components/common';
import { Dispatch, SetStateAction, useState } from 'react';

interface PlanType {
  name: string;
  description: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  features: any;
}
interface CardProps {
  plan: PlanType;
  isExpanded: boolean;
  setExpandedSection: Dispatch<SetStateAction<string | null>>;
}

type ListProps = {
  list: {
    'Community Support': boolean;
    'Exportable user data': boolean;
    'HIPAA Report / BAA': boolean;
    'Priority support': boolean;
    'SOC2 Report': boolean;
    title: string;
  };
};

const buttonStyle = {
  variant: 'white',
  border: '1px solid',
  borderColor: 'gray.100',
  boxShadow: 'none'
};

const FeatureList = ({ list }: ListProps): JSX.Element => {
  return (
    <Flex flexDir="row" w="100%" justifyContent="space-between">
      <List w="100%">
        {Object.entries(list).map((entry, index) => {
          const feature = entry[1];
          return (
            <ListItem
              key={index}
              my={2}
              display="flex"
              flexDir="row"
              justifyContent={'space-between'}
              alignContent="center"
              minH="57px"
              w="100%"
              borderBottom={'1px solid #E4E9F2'}
            >
              <Box>
                {entry[0] !== 'title' ? (
                  <Text
                    py="16px"
                    display="flex"
                    flexDir="row"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {entry[0]}
                    {ToolTips.map(tip => {
                      if (tip.name === entry[0]) {
                        return (
                          <Tooltip
                            key={tip.name}
                            popoverClassName="ml-1.5"
                            panelClassName="left-4 sm:left-10 lg:left-0 !mt-2"
                            buttonClassName="relative"
                            title={
                              <QuestionMarkCircleIcon className="-top-0.5 inline-block h-5 w-5 fill-current text-gray-400" />
                            }
                            content={tip.tooltip}
                          />
                        );
                      }
                    })}
                    {comingSoon.map(feature => {
                      if (feature.name === entry[0]) {
                        return (
                          <Text
                            key={feature.name}
                            fontSize={'xs'}
                            ml={3}
                            px={1}
                            color="primary.500"
                            display="flex"
                            flexDir="row"
                            justifyContent="space-between"
                            bgColor="primary.25"
                            borderRadius="0.25rem"
                            overflow="visible"
                            whiteSpace={'nowrap'}
                          >
                            {feature.text}
                          </Text>
                        );
                      }
                    })}
                  </Text>
                ) : (
                  <Text
                    textAlign="left"
                    my={2}
                    fontWeight={'semibold'}
                    fontSize="lg"
                  >
                    {entry[1]}
                  </Text>
                )}
              </Box>
              {entry[0] === 'title' ? (
                <Box h="60px" bg="transparent"></Box>
              ) : (
                <Box
                  w="200px"
                  display="flex"
                  flexDir="row"
                  justifyContent="flex-end"
                  py="16px"
                >
                  {typeof feature === 'string' ? (
                    <Text textAlign="right">{feature}</Text>
                  ) : feature === true ? (
                    <Icon
                      as={CheckIcon}
                      color={'primary.500'}
                      pos="relative"
                      h="24px"
                    />
                  ) : (
                    <Text>—</Text>
                  )}
                </Box>
              )}
            </ListItem>
          );
        })}
      </List>
    </Flex>
  );
};

const Card = ({
  plan,
  isExpanded,
  setExpandedSection
}: CardProps): JSX.Element => {
  const { name, description, features } = plan;

  return (
    <>
      <Modal
        isOpen={isExpanded}
        onClose={() => setExpandedSection(null)}
        size="full"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalBody
            display="flex"
            flexDir={'column'}
            alignItems="center"
            justifyContent="center"
          >
            <Flex
              flexDir={'column'}
              alignItems="center"
              justifyContent="center"
              mt="-12px"
              mb={'24px'}
              onClick={() => setExpandedSection(null)}
            >
              <Icon
                as={CloseButton}
                color={name == 'Business' ? 'primary.500' : 'gray.700'}
              />

              <Text
                textTransform={'capitalize'}
                fontWeight={700}
                fontSize="11px"
                lineHeight={'24px'}
                mt={'4px'}
              >
                CLOSE ALL FEATURES
              </Text>
            </Flex>{' '}
            <Flex
              direction="column"
              key={name}
              flex={1}
              textAlign="center"
              alignItems="center"
              justifyContent={'center'}
              bg="white"
            >
              <Heading size="h2" fontSize="24px">
                {name} Plan
              </Heading>

              <Text
                fontSize={'13px'}
                mb="18px"
                fontWeight={400}
                letterSpacing="-0.02em"
                lineHeight={'24px'}
                maxW="240px"
              >
                {description}
              </Text>

              {name === 'Enterprise' ? (
                <Button
                  {...buttonStyle}
                  onClick={() => {
                    openPopupWidget({
                      url: URLS.calendlyUrl
                    });
                  }}
                  h="48px"
                >
                  Contact Sales
                </Button>
              ) : (
                <Link
                  legacyBehavior
                  href={process.env.NEXT_PUBLIC_DASHBOARD_URL + '/sign-up'}
                  passHref
                >
                  <Button as="a" {...buttonStyle} h="48px">
                    Start Building Now
                  </Button>
                </Link>
              )}
            </Flex>
            <Box bg="white" w="80vw" pt="40px">
              <FeatureList list={features['Platform pricing']} />
              <FeatureList list={features['Messaging pricing']} />
              <FeatureList list={features['UI & Branding']} />
              <FeatureList list={features['Authentication features']} />
              <FeatureList list={features['User management']} />
              <FeatureList list={features['Session features']} />
              <FeatureList list={features['Organizations features']} />
              <FeatureList list={features['Support & Compliance']} />
            </Box>
          </ModalBody>

          <ModalFooter
            display="flex"
            flexDir="row"
            alignItems="center"
            justifyContent="center"
          >
            <Box mt={'10.5px'} onClick={() => setExpandedSection(null)}>
              <Icon
                as={XIcon}
                h="24px"
                w="24px"
                color={name == 'Business' ? 'primary.500' : 'gray.700'}
              ></Icon>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Center
        mx="24px"
        mt={{ base: 0, lg: '40px' }}
        borderRadius="12px"
        p="32px"
        flexDir="column"
        border={name != 'Business' ? '2px solid #E4E9F2' : '2px solid #694EFF'}
        w="343px"
        h="256px"
        maxH="256px"
        boxShadow={'0px 4px 12px rgba(0, 0, 0, 0.25)'}
        zIndex={1}
        bg="white"
        pos="relative"
      >
        {' '}
        <Flex
          direction="column"
          key={name}
          flex={1}
          textAlign="center"
          alignItems="center"
          justifyContent={'center'}
          bg="white"
        >
          <Heading size="h2" fontSize="24px">
            {name} Plan
          </Heading>

          <Text
            fontSize={'13px'}
            mb="18px"
            fontWeight={400}
            letterSpacing="-0.02em"
            lineHeight={'24px'}
            maxW="250px"
          >
            {description}
          </Text>

          {name === 'Enterprise' ? (
            <Button
              {...buttonStyle}
              onClick={() => {
                openPopupWidget({
                  url: URLS.calendlyUrl
                });
              }}
              h="48px"
            >
              Contact Sales
            </Button>
          ) : (
            <Link
              legacyBehavior
              href={process.env.NEXT_PUBLIC_DASHBOARD_URL + '/sign-up'}
              passHref
            >
              <Button as="a" {...buttonStyle} h="48px">
                Start Building Now
              </Button>
            </Link>
          )}
        </Flex>
        <>
          <Text
            textTransform={'capitalize'}
            fontWeight={700}
            fontSize="11px"
            lineHeight={'24px'}
            mt="20px"
          >
            EXPAND TO EXPLORE ALL FEATURES
          </Text>
          <Box onClick={() => setExpandedSection(name)}>
            <Icon as={PlusIcon} h="18px" w="18px" color="#8F8F8F"></Icon>
          </Box>
        </>
      </Center>
    </>
  );
};

export function PlansTableMobile(): JSX.Element {
  const [expandedSection, setExpandedSection] = useState<string | null>('');

  return (
    <Flex
      flexDir="column"
      bg="white"
      gap="42px"
      justifyContent={'center'}
      alignItems="center"
    >
      {Object.values(plans).map((plan: Plan, index) => (
        <Box key={index}>
          <Card
            plan={plan}
            isExpanded={expandedSection === plan.name}
            setExpandedSection={setExpandedSection}
          />
        </Box>
      ))}
    </Flex>
  );
}
